/**
 * SEO utility functions for managing JSON-LD structured data (schema.org)
 */

/**
 * Get the base URL for schema (works in both server and client contexts)
 */
function getBaseUrl(): string {
  // In browser, use window.location.origin
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  // In server context, use environment variable or default
  if (typeof process !== "undefined" && process.env?.APP_URL) {
    return process.env.APP_URL;
  }
  // Fallback for server-side rendering
  return "https://www.munthlyapp.com";
}

/**
 * Generate Organization schema
 */
export function generateOrganizationSchema() {
  const baseUrl = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Munthly",
    "description": "A free budgeting tool that helps you take control of your finances quickly, easily, and effectively. No subscriptions, no hidden fees, no complicated setup.",
    "url": baseUrl,
    "logo": [
      `${baseUrl}/munthly-logo.svg`,
      `${baseUrl}/munthly-logo-small.svg`
    ],
    "sameAs": [] // Can be populated with social media links if available
  };
}

/**
 * Generate WebSite schema
 */
export function generateWebSiteSchema() {
  const baseUrl = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Munthly",
    "url": baseUrl,
    "description": "A free budgeting tool that helps you take control of your finances with the Munthly Method",
    "publisher": {
      "@type": "Organization",
      "name": "Munthly"
    }
  };
}

/**
 * Generate SoftwareApplication schema
 */
export function generateSoftwareApplicationSchema() {
  const baseUrl = getBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Munthly",
    "description": "A free budgeting tool that helps you take control of your finances. Track income, expenses, and savings with the Munthly Method for a stable monthly balance sheet.",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "url": baseUrl,
    "aggregateRating": undefined // Can be added if reviews are available
  };
}

/**
 * Generate WebPage schema
 */
export function generateWebPageSchema(url: string, title: string, description: string) {
  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": fullUrl,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Munthly",
      "url": baseUrl
    }
  };
}

/**
 * Generate BreadcrumbList schema
 */
export function generateBreadcrumbListSchema(items: Array<{ name: string; url: string }>) {
  const baseUrl = getBaseUrl();
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith("http") ? item.url : `${baseUrl}${item.url}`
    }))
  };
}

/**
 * Generate Article schema (for Method page)
 */
export function generateArticleSchema(url: string, title: string, description: string, datePublished?: string) {
  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;
  
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": fullUrl,
    "publisher": {
      "@type": "Organization",
      "name": "Munthly",
      "url": baseUrl
    }
  };
  
  if (datePublished) {
    schema.datePublished = datePublished;
  }
  
  return schema;
}

/**
 * Add JSON-LD schema script to document head
 * Returns a cleanup function to remove the script
 */
export function addSchemaToPage(schema: object | object[]): () => void {
  if (typeof window === "undefined") {
    return () => {}; // No-op in SSR
  }

  const schemas = Array.isArray(schema) ? schema : [schema];
  const scripts: HTMLScriptElement[] = [];

  schemas.forEach((schemaObj) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schemaObj);
    script.id = `schema-${Date.now()}-${Math.random()}`;
    document.head.appendChild(script);
    scripts.push(script);
  });

  // Return cleanup function
  return () => {
    scripts.forEach((script) => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });
  };
}

/**
 * Remove all schema scripts from document head
 */
export function removeAllSchemas(): void {
  if (typeof window === "undefined") return;
  
  const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach((script) => {
    script.remove();
  });
}

